import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axiosConfig";
import {
  Navbar,
  Container,
  Button,
  Row,
  Col,
  Card,
  Dropdown,
} from "react-bootstrap";
import {
  FiUsers,
  FiUserPlus,
  FiUserCheck,
  FiBriefcase,
  FiUser,
  FiSettings,
  FiTarget,
  FiAward,
  FiMenu,
  FiHome,
  FiArrowRight,
  FiLogOut,
  FiTrendingUp,
} from "react-icons/fi";
import "./AdminDashboard.css";

// Custom Hook to animate numbers counting up
const useCountUp = (end, duration = 1500) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (typeof end !== "number" || isNaN(end)) {
      setCount(0);
      return;
    }
    let start = 0;
    const endValue = parseInt(end, 10);
    if (start === endValue) {
      setCount(endValue);
      return;
    }
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * (endValue - start) + start));
      if (progress < 1) window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);
  }, [end, duration]);
  return count.toLocaleString();
};

// A smaller component for the Roster Card to use the hook correctly.
const RosterCard = ({ userType, counts }) => {
  const navigate = useNavigate();
  const { title, route, icon: IconComponent, key } = userType;
  const totalCount = useCountUp(counts.total);
  const verifiedPercent =
    counts.total > 0 ? Math.round((counts.verified / counts.total) * 100) : 0;

  const handleCardClick = () => {
    navigate(route);
  };

  return (
    <Card
      className="dashboard-card clickable-card"
      onClick={handleCardClick}
      style={{ cursor: "pointer" }}
    >
      <Card.Body>
        <div className="card-header-section">
          <div className="card-icon-wrapper">
            <IconComponent />
          </div>
          <div>
            <h5 className="card-title">{title}</h5>
            <p className="card-subtitle">{totalCount} Total Members</p>
          </div>
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar-labels">
            <span>Verification Progress</span>
            <span>{verifiedPercent}%</span>
          </div>
          <div className="progress-bar-track">
            <div
              className="progress-bar-fill"
              style={{ width: `${verifiedPercent}%` }}
            ></div>
          </div>
        </div>
        <div className="verification-status">
          <div className="verification-item">
            <span className="verification-label">Verified:</span>
            <span className="verification-count verified">
              {counts.verified}
            </span>
          </div>
          <div className="verification-item">
            <span className="verification-label">Unverified:</span>
            <span className="verification-count unverified">
              {counts.unverified}
            </span>
          </div>
        </div>
        <div className="card-footer-action">
          <span>View Team Details</span>
          <FiArrowRight />
        </div>
      </Card.Body>
    </Card>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [verificationCounts, setVerificationCounts] = useState({});

  const userTypes = [
    {
      key: "admin",
      title: "Admins",
      route: "/admins-list",
      icon: FiUserPlus,
    },
    {
      key: "director",
      title: "Sales Heads",
      route: "/directors-list",
      icon: FiBriefcase,
    },
    {
      key: "zonal_head",
      title: "Zonal Managers",
      route: "/zonal-heads-list",
      icon: FiTarget,
    },
    {
      key: "branch_manager",
      title: "Branch Managers",
      route: "/branch-managers-list",
      icon: FiAward,
    },
    {
      key: "team_lead",
      title: "Team Leads",
      route: "/team-leads-list",
      icon: FiUsers,
    },
    {
      key: "relationship_manager",
      title: "RM's",
      route: "/relationship-managers-list",
      icon: FiUser,
    },
  ];

  const registerTypes = [
    {
      title: "Register Admin",
      route: "/register-admin",
      icon: FiUserPlus,
    },
    {
      title: "Register Sales Head",
      route: "/register-director",
      icon: FiUserCheck,
    },
    {
      title: "Register Zonal Manager",
      route: "/register-zonal-head",
      icon: FiBriefcase,
    },
    {
      title: "Register Branch Manager",
      route: "/register-branch-manager",
      icon: FiSettings,
    },
    { title: "Register Team Lead", route: "/register-team-lead", icon: FiUser },
    {
      title: "Register Relationship Manager",
      route: "/register-relationship-manager",
      icon: FiTarget,
    },
  ];

  const menuItems = [
    { label: "Manage All Users", icon: FiUsers, route: "/manage-users" },
    {
      label: "Register Admin",
      icon: FiUserPlus,
      route: "/register-admin",
    },
    {
      label: "Register Sales Head",
      icon: FiTrendingUp,
      route: "/register-director",
    },
    {
      label: "Register Zonal Manager",
      icon: FiTarget,
      route: "/register-zonal-head",
    },
    {
      label: "Register Branch Manager",
      icon: FiUserCheck,
      route: "/register-branch-manager",
    },
    {
      label: "Register Team Lead",
      icon: FiUsers,
      route: "/register-team-lead",
    },
    {
      label: "Register Relationship Manager",
      icon: FiUser,
      route: "/register-relationship-manager",
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        };
        const verificationRes = await api.get("/api/admin/user-counts", {
          headers,
        });
        setVerificationCounts(verificationRes.data);
      } catch (error) {
        console.error("CRITICAL ERROR: Could not fetch user counts.", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const SkeletonCard = () => <div className="skeleton-card" />;

  const handleSidebarNavigate = (route) => {
    setSidebarOpen(false);
    navigate(route);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin-login");
  };

  return (
    <div className="admin-dashboard-solaris">
      <div className={`sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <h3 className="sidebar-title">Admin Menu</h3>
          <Button
            variant="link"
            className="sidebar-close-btn"
            onClick={() => setSidebarOpen(false)}
          >
            <FiMenu size={24} />
          </Button>
        </div>
        <div className="sidebar-buttons">
          {menuItems.map((item, index) => (
            <Button
              key={index}
              variant="light"
              className="sidebar-button"
              onClick={() => handleSidebarNavigate(item.route)}
            >
              <item.icon className="sidebar-button-icon" />
              <span>{item.label}</span>
            </Button>
          ))}
        </div>
      </div>
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      <div className="main-content">
        <Navbar expand="lg" className="dashboard-navbar" sticky="top">
          <Container fluid>
            <div className="d-flex align-items-center">
              <Button
                variant="link"
                className="me-2 d-lg-none menu-toggle-btn"
                onClick={() => setSidebarOpen(true)}
              >
                <FiMenu size={24} />
              </Button>
              <Navbar.Brand className="navbar-brand-custom">
                <FiUser className="me-2" />
                Dashboard
              </Navbar.Brand>
            </div>

            <div className="d-flex align-items-center ms-auto gap-3">
              {/* ======================================================= */}
              {/* ============== THIS IS THE ONLY CHANGE ============== */}
              {/* ======================================================= */}
              <Button
                className="nav-btn btn-gradient-blue-purple"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                <FiUserPlus />
                <span>Register User</span>
              </Button>
              {/* ======================================================= */}

              <Dropdown>
                <Dropdown.Toggle
                  className="nav-btn btn-solid-purple"
                  id="performance-dropdown"
                >
                  <FiTarget />
                  <div className="btn-text-stacked">
                    <span>Performance</span>
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => navigate("/performance-statistics")}
                  >
                    <FiTarget className="me-2" />
                    Performance Statistics
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() =>
                      navigate("/admin/monthly-performance-reports")
                    }
                  >
                    <FiAward className="me-2" />
                    Performance Reports
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => navigate("/admin-report")}>
                    <FiUsers className="me-2" />
                    Admin Report
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => navigate("/team-management")}>
                    <FiBriefcase className="me-2" />
                    Team Management
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              <Button
                className="nav-btn btn-gradient-blue-purple"
                onClick={() => navigate("/admin-profile")}
              >
                <FiUser />
                <span>Profile</span>
              </Button>
            </div>
          </Container>
        </Navbar>

        <Container fluid className="dashboard-body">
          <h2 className="section-title">Register Users</h2>
          <Row className="g-4 mb-5">
            {registerTypes.map((registerType) => (
              <Col xl={4} lg={6} key={registerType.route}>
                <Card
                  className="dashboard-card"
                  onClick={() => navigate(registerType.route)}
                  style={{ cursor: "pointer" }}
                >
                  <Card.Body>
                    <div className="card-header-section">
                      <div className="card-icon-wrapper">
                        <registerType.icon />
                      </div>
                      <div>
                        <h5 className="card-title">{registerType.title}</h5>
                        <p className="card-subtitle">
                          Add new user to the system
                        </p>
                      </div>
                    </div>
                    <div className="card-footer-action">
                      <span>Register Now</span>
                      <FiArrowRight />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <h2 className="section-title">Team Roster</h2>
          <Row className="g-4">
            {loading
              ? [...Array(5)].map((_, i) => (
                  <Col xl={4} lg={6} key={i}>
                    <SkeletonCard />
                  </Col>
                ))
              : userTypes.map((userType) => {
                  const counts = verificationCounts[userType.key] || {
                    total: 0,
                    verified: 0,
                  };
                  return (
                    <Col xl={4} lg={6} key={userType.key}>
                      <RosterCard userType={userType} counts={counts} />
                    </Col>
                  );
                })}
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default AdminDashboard;
